import React, { useEffect, useState, useRef } from "react";
import { Box, Typography, Button, IconButton } from "@mui/joy";
import { Favorite } from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

const POLL_INTERVAL = 2000;
const HEART_ANIM_MS = 1200;

function VotingBar({ eventId }) {
  const { t } = useTranslation();
  const [votes, setVotes] = useState({ boy: 0, girl: 0 });
  const [voted, setVoted] = useState(() => localStorage.getItem(`vote-${eventId}`));
  const [isVoting, setIsVoting] = useState(false);

  const fetchVotes = async () => {
    const res = await fetch(`https://revealoo.com/api/get_votes.php?eventId=${eventId}`);
    const data = await res.json();
    setVotes({ boy: data.boy, girl: data.girl });
  };

  useEffect(() => {
    fetchVotes();
    const timer = setInterval(fetchVotes, POLL_INTERVAL);
    return () => clearInterval(timer);
  }, [eventId]);

  const handleVote = async (side) => {
    if (voted) return;
    setIsVoting(true);
    await fetch(`https://revealoo.com/api/vote.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ eventId, vote: side }),
    });
    setVoted(side);
    localStorage.setItem(`vote-${eventId}`, side);
    setIsVoting(false);
    fetchVotes();
  };

  const total = votes.boy + votes.girl;
  const percentBoy = total === 0 ? 50 : Math.round((votes.boy / total) * 100);
  const percentGirl = 100 - percentBoy;

  return (
    <Box sx={{ my: 3, p: 2, borderRadius: 4, background: "#f7f8fa", boxShadow: "0 2px 8px #c2e1ff33" }}>
      <Typography level="body-md" sx={{ mb: 1, color: "#5e79b8" }}>
        {t("misc.guessGender")}
      </Typography>
      <Box sx={{ display: "flex", gap: 2, justifyContent: "center", mb: 2 }}>
        <Button
          color="primary"
          disabled={!!voted || isVoting}
          onClick={() => handleVote("boy")}
          variant={voted === "boy" ? "solid" : "soft"}
        >
          👦 {t("misc.boy")}
        </Button>
        <Button
          color="danger"
          disabled={!!voted || isVoting}
          onClick={() => handleVote("girl")}
          variant={voted === "girl" ? "solid" : "soft"}
        >
          👧 {t("misc.girl")}
        </Button>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography sx={{ color: "#1976d2", minWidth: 38 }}>{votes.boy}</Typography>
        <Box sx={{ flex: 1, mx: 1, position: "relative", height: 20, borderRadius: 7, overflow: "hidden", background: "#e3f2fd" }}>
          {/* Szala */}
          <motion.div
            style={{
              position: "absolute",
              left: 0,
              height: "100%",
              width: `${percentBoy}%`,
              background: "linear-gradient(90deg, #2196f3 80%, #e3f2fd 100%)",
              borderRadius: "7px 0 0 7px",
              transition: "width .4s cubic-bezier(.5,2,.6,1)",
            }}
          />
          <motion.div
            style={{
              position: "absolute",
              right: 0,
              height: "100%",
              width: `${percentGirl}%`,
              background: "linear-gradient(90deg, #fff0f6 0%, #ec407a 90%)",
              borderRadius: "0 7px 7px 0",
              transition: "width .4s cubic-bezier(.5,2,.6,1)",
            }}
          />
        </Box>
        <Typography sx={{ color: "#ec407a", minWidth: 38, textAlign: "right" }}>{votes.girl}</Typography>
      </Box>
      <Typography sx={{ mt: 1.2, color: "#2d3c88", fontWeight: 700 }}>
        {percentBoy}% 👦 / {percentGirl}% 👧
      </Typography>
    </Box>
  );
}

// ---- Serduszka live ----

export function LiveHearts({ eventId }) {
  const { t } = useTranslation();
  const [hearts, setHearts] = useState([]); // {id: string, key: string}
  const [count, setCount] = useState(0);
  const lastCount = useRef(0);
  const animTimeouts = useRef([]);
  const loadedInitial = useRef(false);

  // Funkcja do animacji serca
  const spawnHeart = () => {
    const key = Math.random().toString(36).substr(2, 9);
    setHearts((arr) => [...arr, key]);
    const t = setTimeout(() => {
      setHearts((arr) => arr.filter((h) => h !== key));
    }, HEART_ANIM_MS);
    animTimeouts.current.push(t);
  };

  // Po wejściu na stronę: animuj wszystkie lajki
  useEffect(() => {
    const fetchLikes = async () => {
      const res = await fetch(`https://revealoo.com/api/get_likes.php?eventId=${eventId}`);
      const data = await res.json();
      if (typeof data.count === "number") {
        setCount(data.count);
        if (!loadedInitial.current) {
          // Pierwsze wejście – animuj wszystkie
          for (let i = 0; i < data.count; i++) {
            setTimeout(() => {
              spawnHeart();
            }, i * 220);
          }
          lastCount.current = data.count;
          loadedInitial.current = true;
        } else if (data.count > lastCount.current) {
          // Przybyły nowe lajki od ostatniego fetchu
          const diff = data.count - lastCount.current;
          for (let i = 0; i < diff; i++) {
            setTimeout(() => {
              spawnHeart();
            }, i * 220);
          }
          lastCount.current = data.count;
        }
        // Jeśli count zmalał (nie powinien), to ignorujemy
      }
    };
    fetchLikes();
    const interval = setInterval(fetchLikes, 8000);
    return () => clearInterval(interval);
  }, [eventId]);

  // Kliknięcie serca – animuje natychmiast + zapisuje do bazy
  const handleLike = async () => {
    spawnHeart();
    setCount((prev) => prev + 1);   // OPTYMISTYCZNY update licznika!
    lastCount.current += 1;         // Też aktualizuj ref!
    await fetch(`https://revealoo.com/api/like.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ eventId }),
    });
  };

  // Po opuszczeniu strony – czyść timeouty
  useEffect(() => () => animTimeouts.current.forEach(clearTimeout), []);

  // Poziom yStart/yEnd/rozrzut, żeby serca się trochę "bujały"
  const randomX = () => (Math.random() - 0.5) * 28; // +- px
  const randomSize = () => 1 + Math.random() * 0.33;

  return (
    <Box sx={{ position: "relative", minHeight: 46, mt: 2, mb: 2, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <IconButton onClick={handleLike} size="lg" color="danger" sx={{ fontSize: 28, mr: 2, background: "#fff0f6" }}>
        <Favorite sx={{ fontSize: 36, color: "#ec407a" }} />
      </IconButton>
      <Typography level="body-md" sx={{ color: "#ec407a", fontWeight: 700, fontSize: "1.09rem" }}>
        {t("misc.likeThisEvent")} {count > 0 && <span>❤️ {count}</span>}
      </Typography>
      {/* Animowane serduszka */}
      <Box sx={{ position: "absolute", left: 28, bottom: 6, pointerEvents: "none", width: 0, height: 0 }}>
        <AnimatePresence>
          {hearts.map((key) => (
            <motion.div
              key={key}
              initial={{
                opacity: 0.8,
                scale: randomSize(),
                x: randomX(),
                y: 0,
                rotate: (Math.random() - 0.5) * 25,
              }}
              animate={{
                opacity: [0.9, 1, 0.5, 0],
                y: -92 - Math.random() * 40,
                x: randomX(),
                scale: randomSize(),
                rotate: (Math.random() - 0.5) * 40,
                transition: { duration: HEART_ANIM_MS / 1000, ease: "easeOut" }
              }}
              exit={{ opacity: 0, scale: 0.8, transition: { duration: 0.24 } }}
              style={{
                position: "absolute",
                left: 0,
                bottom: 0,
                fontSize: 34 + Math.random() * 14,
                color: "#ec407a",
                pointerEvents: "none",
                zIndex: 10,
                filter: "drop-shadow(0 2px 7px #faa7f941)",
                userSelect: "none",
              }}
            >❤️</motion.div>
          ))}
        </AnimatePresence>
      </Box>
    </Box>
  );
}

export { VotingBar };
